/**
 * The Toukky and Toukky Partner apps are this same site inside a Capacitor
 * web view (docs/mobile/PLAN.md). The native shell injects `window.Capacitor`
 * and registers its plugins there; the site never imports a Capacitor package,
 * it only reads that global, and does nothing when it is absent — which is the
 * case in every browser.
 */

// Which app a build is for, fixed at build time. The partner app opens on the
// partner portal, as partners.toukky.com does in a browser.
export const APP_TARGET = process.env.REACT_APP_APP_TARGET === "partner" ? "partner" : "customer";

export function isNativeApp() {
  try {
    return Boolean(window.Capacitor?.isNativePlatform?.());
  } catch {
    return false;
  }
}

/** A native plugin by its registered name (App, Browser, SplashScreen, …), or null. */
export function nativePlugin(name) {
  if (!isNativeApp()) return null;
  return window.Capacitor?.Plugins?.[name] || null;
}

/** An http(s) address on another origin than the page's. */
export function isExternalUrl(href, origin = window.location.origin) {
  try {
    const url = new URL(href, origin);
    return (url.protocol === "https:" || url.protocol === "http:") && url.origin !== origin;
  } catch {
    return false;
  }
}

/** Opens an address in the phone's browser; false when not in the app. */
export async function openExternal(url) {
  const Browser = nativePlugin("Browser");
  if (!Browser?.open) return false;
  await Browser.open({ url });
  return true;
}

/**
 * The in-app route a deep link points to: `toukky://booking/123`,
 * `toukkypartner://partner/dashboard?tab=documents` or
 * `https://www.toukky.com/booking/123` all become a path and query.
 */
export function appPathFromUrl(link) {
  try {
    const url = new URL(link);
    const web = url.protocol === "https:" && ["www.toukky.com", "toukky.com", "partners.toukky.com"].includes(url.host);
    const native = url.protocol === "toukky:" || url.protocol === "toukkypartner:";
    if ((!web && !native) || url.username || url.password || url.port) return null;
    const raw = web ? url.pathname : `/${url.host}${url.pathname}`;
    const path = raw.replace(/\/{2,}/g, "/").replace(/(.)\/$/, "$1") || "/";
    return `${path}${url.search}`;
  } catch {
    return null;
  }
}
